import { useEffect, useState } from 'react'

import { activeKeySet, type PieSegment } from '../../lib/composition'

interface CompositionDonutProps {
  /** Slices in render order (clockwise from 12 o'clock). */
  segments: PieSegment[]
  /** Arc denominator. */
  total: number
  /** Big center text — severity: String(total); coverage: "26%" / "—". */
  centerValue: string
  /** Small caps sub-label under the value — severity: "IN VIEW"; coverage: "COVERAGE". */
  centerLabel?: string
  /** Segment key(s) to spotlight; '' or [] = none (all full opacity). Accepts one key or many. */
  active?: string | readonly string[]
  /** Click an arc → toggle its key; omit for a static donut. */
  onToggle?: (key: string) => void
  /** Box size in px. Default 140. */
  size?: number
  /** Inter-arc gap in circumference units. Default 0 (gapless); coverage passes 2. */
  gap?: number
  /** Center value font size in px. Default 34 (Coverage); the Issues severity donut passes a smaller value. */
  centerValueSize?: number
}

const R = 46
const C = 2 * Math.PI * R

/**
 * Generic segment-driven 140px donut (extracted from `IssueSeverityDonut`'s size-path). Arcs sized by
 * `count/total` with `strokeDasharray`/`strokeDashoffset` accumulation, rotated -90°. Inactive arcs dim
 * to opacity .25 when a facet is active. A transparent fat overlay circle provides the click target when
 * `onToggle` is set. `gap > 0` carves a small separator between arcs (the mock's 2px gaps). The legend is
 * NOT rendered here — the owning card draws it (parallel to `SeverityLoadCard`).
 */
export function CompositionDonut({
  segments,
  total,
  centerValue,
  centerLabel,
  active = '',
  onToggle,
  size = 140,
  gap = 0,
  centerValueSize = 34,
}: CompositionDonutProps) {
  // Draw-in entrance: start with all arcs invisible (strokeDashoffset = full circumference)
  // and transition to the real offset on mount. Flipping `mounting` to false in useEffect
  // ensures the first paint starts from the empty state before CSS transitions to the real value.
  const [mounting, setMounting] = useState(true)
  useEffect(() => {
    setMounting(false)
  }, [segments])

  const hasData = total > 0 && segments.some((s) => s.count > 0)
  const activeSet = activeKeySet(active)
  let offset = 0
  return (
    <div className="relative flex-none" style={{ width: size, height: size }}>
      <svg
        viewBox="0 0 120 120"
        className="h-full w-full"
        role="img"
        aria-label={`${centerLabel ?? 'Total'} ${centerValue}`}
      >
        <circle cx={60} cy={60} r={R} fill="none" stroke="var(--gis-track-2)" strokeWidth={15} />
        {hasData &&
          segments.map((s) => {
            if (s.count <= 0) {
              return null
            }
            const len = (s.count / total) * C
            // Carve the inter-arc gap from the end of each arc; clamp so a sub-gap sliver vanishes
            // cleanly (never a negative dash) rather than relying on SVG's negative-length handling.
            const visible = gap > 0 ? Math.max(0, len - gap) : len
            const dash = `${visible} ${C - visible}`
            const off = offset
            offset += len
            // Draw-in: while mounting, push the arc fully off-screen (dashoffset = circumference +
            // the segment's natural offset) so it starts invisible; on mount the offset returns to
            // -off and the transition animates the arc into view.
            const drawInOffset = mounting ? -(C + off) : -off
            return (
              <g key={s.key}>
                <circle
                  cx={60}
                  cy={60}
                  r={R}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={15}
                  strokeDasharray={dash}
                  strokeDashoffset={drawInOffset}
                  transform="rotate(-90 60 60)"
                  opacity={activeSet === null || activeSet.has(s.key) ? 1 : 0.25}
                  className="motion-reduce:transition-none"
                  style={{
                    transition: 'stroke-dashoffset 550ms cubic-bezier(.2,.7,.3,1), opacity .2s',
                    ...(onToggle ? { cursor: 'pointer' } : {}),
                  }}
                >
                  <title>{`${s.label}: ${s.count}${onToggle ? ' — click to filter' : ''}`}</title>
                </circle>
                {onToggle && (
                  <circle
                    cx={60}
                    cy={60}
                    r={R}
                    fill="none"
                    stroke="#000"
                    strokeOpacity={0}
                    strokeWidth={24}
                    strokeDasharray={dash}
                    strokeDashoffset={-off}
                    transform="rotate(-90 60 60)"
                    pointerEvents="stroke"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onToggle(s.key)}
                  />
                )}
              </g>
            )
          })}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <b className="font-bold leading-none tracking-[-.02em] tabular-nums text-gis-ink" style={{ fontSize: centerValueSize }}>
          {centerValue}
        </b>
        {centerLabel && (
          <small className="mt-[3px] text-[10px] font-bold tracking-[.1em] text-gis-muted">
            {centerLabel}
          </small>
        )}
      </div>
    </div>
  )
}
